import { useEffect, useState } from 'react';

import { Col, Form } from 'antd';
import { Formik, FormikProps } from 'formik';
import { isUndefined } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import {
  doClearCreateAccountHolderFinancialInformationEmployment,
  doClearPatchAccountHolderFinancialInformationEmployment,
  doCreateAccountHolderFinancialInformationEmployment,
  doPatchAccountHolderFinancialInformationEmployment,
} from 'src/actions';
import { ErrorName, US_COUNTRY_LABEL, US_COUNTRY_STATE_LIST, US_COUNTRY_VALUE } from 'src/constants';
import { AccountHolderEmploymentStatusDto, AccountHolderYearsEmployedDto } from 'src/dtos';
import { ErrorResponseAlert, MFormInput, MFormSaveButton, MFormSelect } from 'src/lib';
import { AccountHolder, AccountHolderEmploymentStatusLabel, AccountHolderYearsEmployedLabel } from 'src/models';
import { assertNonNullable } from 'src/utils';

import * as Styles from './FinancialInformation.styles';
import { upsertAccountHolderFinancialInformationEmploymentValidation } from './validations';

export const countryOptions = [{ label: US_COUNTRY_LABEL, value: US_COUNTRY_VALUE }];

export const EMPLOYMENT_STATUS_OPTION_LIST = [
  {
    value: AccountHolderEmploymentStatusDto.Employed,
    label: AccountHolderEmploymentStatusLabel.Employed,
  },
  {
    value: AccountHolderEmploymentStatusDto.Retired,
    label: AccountHolderEmploymentStatusLabel.Retired,
  },
  {
    value: AccountHolderEmploymentStatusDto.Unemployed,
    label: AccountHolderEmploymentStatusLabel.Unemployed,
  },
  {
    value: AccountHolderEmploymentStatusDto.Student,
    label: AccountHolderEmploymentStatusLabel.Student,
  },
];

export const YEARS_EMPLOYED_OPTION_LIST = [
  {
    value: AccountHolderYearsEmployedDto.BetweenZeroAndOne,
    label: AccountHolderYearsEmployedLabel.BetweenZeroAndOne,
  },
  {
    value: AccountHolderYearsEmployedDto.BetweenTwoAndFive,
    label: AccountHolderYearsEmployedLabel.BetweenTwoAndFive,
  },
  {
    value: AccountHolderYearsEmployedDto.BetweenFiveAndTen,
    label: AccountHolderYearsEmployedLabel.BetweenFiveAndTen,
  },
  {
    value: AccountHolderYearsEmployedDto.BetweenTenAndTwenty,
    label: AccountHolderYearsEmployedLabel.BetweenTenAndTwenty,
  },
  {
    value: AccountHolderYearsEmployedDto.MoreThanTwenty,
    label: AccountHolderYearsEmployedLabel.MoreThanTwenty,
  },
];

export interface FinancialInformationEmploymentFormValues {
  employmentStatus?: AccountHolderEmploymentStatusDto;
  employerName?: string;
  jobTitle?: string;
  yearsEmployed?: AccountHolderYearsEmployedDto;
  address1?: string;
  address2?: string;
  country?: string;
  state?: string;
  city?: string;
  postalCode?: string;
}

export interface FinancialInformationEmploymentFormProps {
  accountUuid?: string;
  accountHolder?: AccountHolder;
  onCancel?: () => void;
  onSave?: () => void;
  testId?: 'primary' | 'joint';
}

export const FinancialInformationEmploymentForm = ({
  accountHolder,
  accountUuid,
  onCancel,
  onSave,
  testId,
}: FinancialInformationEmploymentFormProps) => {
  const dispatch = useDispatch();

  const isCreateFinancialInformationEmploymentLoading = useSelector((state: any) =>
    Boolean(state.accountHolders.createFinancialInformationEmployment.__requested),
  );
  const succeededCreateFinancialInformationEmployment = useSelector((state: any) =>
    Boolean(state.accountHolders.createFinancialInformationEmployment.__succeeded),
  );
  const createError = useSelector((state: any) => state.accountHolders.createFinancialInformationEmployment.error);

  const isPatchFinancialInformationEmploymentLoading = useSelector((state: any) =>
    Boolean(state.accountHolders.patchFinancialInformationEmployment.__requested),
  );
  const succeededPatchFinancialInformationEmployment = useSelector((state: any) =>
    Boolean(state.accountHolders.patchFinancialInformationEmployment.__succeeded),
  );
  const patchError = useSelector((state: any) => state.accountHolders.patchFinancialInformationEmployment.error);

  const createRequestData = useSelector(
    (state: any) => state.accountHolders.createFinancialInformationEmployment?.data,
  );
  const patchRequestData = useSelector((state: any) => state.accountHolders.patchFinancialInformationEmployment?.data);

  const [initialValues, setInitialValues] = useState<FinancialInformationEmploymentFormValues>({});
  const [shouldPatch, setShouldPatch] = useState<boolean>(false);
  const [isAddressPopulated, setIsAddressPopulated] = useState<boolean>(false);

  const isFinancialInformationEmploymentAlreadySaved = () =>
    !isUndefined(accountHolder?.financialInformation?.employmentStatus);

  const isRequestMatched = () =>
    accountHolder?.id === createRequestData?.params?.accountHolderId ||
    accountHolder?.id === patchRequestData?.params?.accountHolderId;

  const isUpsertLoading =
    (isCreateFinancialInformationEmploymentLoading || isPatchFinancialInformationEmploymentLoading) &&
    isRequestMatched();

  const isEmployedStatusSelected = (value?: string) => value === AccountHolderEmploymentStatusDto.Employed;

  const _onCancel = (form: FormikProps<FinancialInformationEmploymentFormValues>) => {
    form.resetForm();

    if (onCancel) {
      onCancel();
    }
  };

  const _onSave = (form: FormikProps<FinancialInformationEmploymentFormValues>) => {
    form.submitForm();

    if (onSave) {
      onSave();
    }
  };

  useEffect(() => {
    setInitialValues({
      employmentStatus: accountHolder?.financialInformation?.employmentStatus?.value,
      jobTitle: accountHolder?.financialInformation?.jobTitle,
      yearsEmployed: accountHolder?.financialInformation?.yearsEmployed?.value,
      employerName: accountHolder?.financialInformation?.employerName,
      address1: accountHolder?.financialInformation?.employerAddress?.address1,
      address2: accountHolder?.financialInformation?.employerAddress?.address2,
      country: accountHolder?.financialInformation?.employerAddress?.country?.value ?? US_COUNTRY_VALUE,
      state: accountHolder?.financialInformation?.employerAddress?.state?.value,
      city: accountHolder?.financialInformation?.employerAddress?.city,
      postalCode: accountHolder?.financialInformation?.employerAddress?.postalCode,
    });
  }, [accountHolder]);

  useEffect(() => {
    if (isFinancialInformationEmploymentAlreadySaved()) {
      setShouldPatch(true);
    }
  }, [accountHolder]);

  useEffect(() => {
    if (
      (succeededCreateFinancialInformationEmployment || succeededPatchFinancialInformationEmployment) &&
      isRequestMatched()
    ) {
      if (onCancel) {
        onCancel();
      }
    }
  }, [succeededCreateFinancialInformationEmployment, succeededPatchFinancialInformationEmployment]);

  useEffect(() => {
    return () => {
      dispatch(doClearCreateAccountHolderFinancialInformationEmployment());
      dispatch(doClearPatchAccountHolderFinancialInformationEmployment());
    };
  }, []);

  return (
    <Formik
      enableReinitialize
      validateOnChange
      validateOnBlur
      onSubmit={values => {
        assertNonNullable(accountHolder, 'AccountHolder');
        assertNonNullable(accountUuid, 'accountUuid');

        const dto =
          values.employmentStatus === AccountHolderEmploymentStatusDto.Employed
            ? {
                employmentStatus: values.employmentStatus,
                employerName: values.employerName,
                jobTitle: values.jobTitle,
                yearsEmployed: values.yearsEmployed,
                employerAddress: {
                  address1: values.address1,
                  address2: values.address2,
                  city: values.city,
                  country: values.country,
                  state: values.state,
                  postalCode: values.postalCode,
                },
              }
            : {
                employmentStatus: values.employmentStatus,
              };

        if (shouldPatch) {
          dispatch(
            doPatchAccountHolderFinancialInformationEmployment({
              params: {
                id: accountUuid,
                accountHolderId: accountHolder.id,
              },
              body: dto as any, // TODO: fix the type casting
            }),
          );

          return;
        }

        dispatch(
          doCreateAccountHolderFinancialInformationEmployment({
            params: {
              id: accountUuid,
              accountHolderId: accountHolder.id,
            },
            body: dto as any, // TODO: fix the type casting
          }),
        );
      }}
      initialValues={initialValues}
      validationSchema={upsertAccountHolderFinancialInformationEmploymentValidation}>
      {form => {
        const onPopulateFinancialInformationEmployerAddress = () => {
          if (createError?.error === ErrorName.UspsAddressMismatch) {
            const message = JSON.parse(createError?.message);
            form.setFieldValue('address1', message.address.address1);
            form.setFieldValue('address2', message.address.address2);
            form.setFieldValue('city', message.address.city);
            form.setFieldValue('state', message.address.state);
            form.setFieldValue('postalCode', message.address.postalCode);
            setIsAddressPopulated(true);
          }

          if (patchError?.error === ErrorName.UspsAddressMismatch) {
            const message = JSON.parse(patchError?.message);
            form.setFieldValue('address1', message.address.address1);
            form.setFieldValue('address2', message.address.address2);
            form.setFieldValue('city', message.address.city);
            form.setFieldValue('state', message.address.state);
            form.setFieldValue('postalCode', message.address.postalCode);
            setIsAddressPopulated(true);
          }
        };

        useEffect(() => {
          setIsAddressPopulated(false);
        }, [createError, patchError]);

        return (
          <Form autoComplete='off' style={{ width: '100%' }}>
            <Col span={24}>
              <MFormSelect
                label='Employment Status'
                placeholder='Select'
                defaultValue={accountHolder?.financialInformation?.employmentStatus?.value}
                options={EMPLOYMENT_STATUS_OPTION_LIST}
                error={form.errors.employmentStatus}
                onChange={value => {
                  form.setFieldValue('employmentStatus', value);
                }}
                name='financial-employment-status-name-attribute'
                autoComplete='off'
                testId={`${testId}-account-employment-status`}
              />
            </Col>

            {isEmployedStatusSelected(form.values.employmentStatus) && (
              <>
                <Col span={24}>
                  <MFormInput
                    label='Employer Name'
                    placeholder='Enter'
                    defaultValue={accountHolder?.financialInformation?.employerName}
                    error={form.errors.employerName}
                    onChange={value => {
                      form.setFieldValue('employerName', value);
                    }}
                    name='financial-employer-name-attribute'
                    autoComplete='off'
                    testId={`${testId}-account-employer-name`}
                  />
                </Col>
                <Col span={24}>
                  <MFormInput
                    label='Job Title'
                    placeholder='Enter'
                    defaultValue={accountHolder?.financialInformation?.jobTitle}
                    error={form.errors.jobTitle}
                    onChange={value => {
                      form.setFieldValue('jobTitle', value);
                    }}
                    name='financial-employment-job-title-name-attribute'
                    autoComplete='off'
                    testId={`${testId}-account-job-title`}
                  />
                </Col>
                <Col span={24}>
                  <MFormSelect
                    label='Years Employed'
                    placeholder='Select'
                    defaultValue={accountHolder?.financialInformation?.yearsEmployed?.value}
                    options={YEARS_EMPLOYED_OPTION_LIST}
                    error={form.errors.yearsEmployed}
                    onChange={value => {
                      form.setFieldValue('yearsEmployed', value);
                    }}
                    name='financial-employment-years-employed-name-attribute'
                    autoComplete='off'
                    testId={`${testId}-account-years-employed`}
                  />
                </Col>
                <Col span={24} className={Styles.title}>
                  Employment Address
                </Col>
                <Col span={24}>
                  <MFormInput
                    label='Address Line 1'
                    placeholder='Enter'
                    value={form.values.address1}
                    defaultValue={accountHolder?.financialInformation?.employerAddress?.address1}
                    error={form.errors?.address1}
                    onChange={value => {
                      form.setFieldValue('address1', value);
                    }}
                    name='financial-employment-address1-name-attribute'
                    autoComplete='off'
                    testId={`${testId}-account-mailing-address1`}
                  />
                </Col>
                <Col span={24}>
                  <MFormInput
                    label='Address Line 2 (Opt.)'
                    placeholder='Enter'
                    value={form.values.address2}
                    defaultValue={accountHolder?.financialInformation?.employerAddress?.address2}
                    error={form.errors.address2}
                    onChange={value => {
                      form.setFieldValue('address2', value);
                    }}
                    name='financial-employment-address2-name-attribute'
                    autoComplete='off'
                    testId={`${testId}-account-mailing-address2`}
                  />
                </Col>
                <Col span={24}>
                  <MFormSelect
                    label='Country'
                    placeholder='Select'
                    defaultValue={
                      accountHolder?.financialInformation?.employerAddress?.country?.value ?? US_COUNTRY_VALUE
                    }
                    options={countryOptions}
                    error={form.errors?.country}
                    onChange={value => {
                      form.setFieldValue('country', value);
                    }}
                    name='financial-employment-country-name-attribute'
                    autoComplete='off'
                    testId={`${testId}-account-mailing-country`}
                  />
                </Col>
                <Col span={24}>
                  <MFormInput
                    label='City'
                    placeholder='Enter'
                    value={form.values.city}
                    defaultValue={accountHolder?.financialInformation?.employerAddress?.city}
                    error={form.errors?.city}
                    onChange={value => {
                      form.setFieldValue('city', value);
                    }}
                    name='financial-employment-city-name-attribute'
                    autoComplete='off'
                    testId={`${testId}-account-mailing-city`}
                  />
                </Col>
                <Col span={24}>
                  <MFormSelect
                    label='State'
                    placeholder='Select'
                    defaultValue={accountHolder?.financialInformation?.employerAddress?.state?.value}
                    value={form.values.state}
                    options={US_COUNTRY_STATE_LIST}
                    error={form.errors?.state}
                    onChange={value => {
                      form.setFieldValue('state', value);
                    }}
                    name='financial-employment-state-name-attribute'
                    autoComplete='off'
                    testId={`${testId}-account-mailing-state`}
                  />
                </Col>
                <Col span={24}>
                  <MFormInput
                    label='Postal Code'
                    placeholder='Enter'
                    value={form.values.postalCode}
                    defaultValue={accountHolder?.financialInformation?.employerAddress?.postalCode}
                    error={form.errors?.postalCode}
                    onChange={value => {
                      form.setFieldValue('postalCode', value);
                    }}
                    name='financial-employment-postal-code-name-attribute'
                    autoComplete='off'
                    testId={`${testId}-account-mailing-postal-code`}
                  />
                </Col>
              </>
            )}
            {!isAddressPopulated && isRequestMatched() && (
              <Col span={24}>
                <ErrorResponseAlert
                  error={createError ?? patchError}
                  name='employment'
                  onClick={onPopulateFinancialInformationEmployerAddress}
                />
              </Col>
            )}
            <Col span={24}>
              <MFormSaveButton<FinancialInformationEmploymentFormValues>
                loading={isUpsertLoading}
                onCancel={_onCancel}
                onSave={_onSave}
                isEditMode={shouldPatch}
                form={form}
                testId={`${testId}-employment-information`}
              />
            </Col>
          </Form>
        );
      }}
    </Formik>
  );
};
